/**
 * SEO Specialist module — request validators.
 *
 * Surface lands across PRs 2 → 4. v1.7 PR 2 ships:
 *   - SeoConfigInputSchema  → PATCH /admin/seo/config
 *   - SeoKeyUpdateSchema    → PATCH /admin/seo/keys/:provider
 *   - GrantSeoBodySchema    → POST  /admin/seo/access
 *
 * Provider/depth enums are reused by later PRs, so they live here too.
 */
import { z } from 'zod';
export const SeoProviderSchema = z.enum(['openai', 'anthropic', 'deepseek', 'gemini']);
export const TavilyDepthSchema = z.enum(['basic', 'advanced']);
/**
 * v2.0.0 PR #B — provider / model / temperature / maxOutputTokens /
 * customSystemPrompt all moved to the SEO AgentProfile (editable via
 * Settings → Agent Profiles → SEO Specialist / SEO Playbook Generator).
 * What remains here is non-LLM SEO config: crawl targets + Tavily knobs.
 */
export const SeoConfigInputSchema = z
    .object({
    websiteUrl: z.string().url().max(2000),
    sitemapUrl: z.string().url().max(2000).nullable(),
    crawlDepth: z.number().int().min(1).max(3),
    crawlPageLimit: z.number().int().min(10).max(200),
    geographicFocus: z.array(z.string().min(2).max(2)).max(20),
    industry: z.string().min(1).max(120).nullable(),
    brandNames: z.array(z.string().min(1).max(80)).max(20),
    competitorUrls: z.array(z.string().url().max(2000)).max(20),
    tavilySearchDepth: TavilyDepthSchema,
    tavilyMaxQueriesPerAnalysis: z.number().int().min(10).max(100),
})
    .partial()
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
/**
 * Tavily key set/update body — same shape as the dropped per-LLM key
 * endpoint, but now scoped to Tavily alone (the only key still
 * SEO-owned after the v2.0.0 PR #B migration).
 */
export const SeoTavilyKeyUpdateSchema = z.object({
    apiKey: z.string().trim().min(8).max(500),
});
export const GrantSeoBodySchema = z.object({
    userId: z.string().uuid(),
});
// Analysis pipeline (v1.7 PR 3) — request validators
export const SeoAnalysisStatusSchema = z.enum([
    'queued',
    'discovering',
    'crawling',
    'probing',
    'scoring',
    'completed',
    'failed',
]);
export const SeoDimensionSchema = z.enum(['classical', 'geo', 'ai']);
export const SeoPrioritySchema = z.enum(['critical', 'high', 'medium', 'low']);
export const SeoEffortSchema = z.enum(['quick_win', 'medium', 'large']);
export const SeoEngineSchema = z.enum([
    'chatgpt',
    'perplexity',
    'google_aio',
    'claude',
    'gemini',
]);
export const SeoSuggestionStatusSchema = z.enum([
    'open',
    'in_progress',
    'done',
    'dismissed',
]);
export const SeoAnalysisIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const SeoSuggestionIdParamSchema = z.object({
    id: z.string().uuid(),
});
export const SeoSuggestionFiltersSchema = z
    .object({
    analysisId: z.string().uuid().optional(),
    dimension: SeoDimensionSchema.optional(),
    priority: SeoPrioritySchema.optional(),
    status: SeoSuggestionStatusSchema.optional(),
})
    .partial();
export const SeoSuggestionStatusUpdateSchema = z.object({
    status: SeoSuggestionStatusSchema,
});
// Playbook (v1.7.2) — request validators + post-response Zod parse used to
// validate the LLM's raw JSON BEFORE we trust it. Length / count caps live
// here, NOT in the strict-mode jsonSchema we hand to OpenAI.
export const SeoPlaybookSnippetKindSchema = z.enum([
    'html',
    'meta',
    'json_ld',
    'markdown',
    'text',
]);
export const SeoPlaybookSnippetSchema = z.object({
    kind: SeoPlaybookSnippetKindSchema,
    label: z.string().min(1).max(120),
    code: z.string().min(1).max(8000),
});
export const SeoPlaybookStepSchema = z.object({
    order: z.number().int().min(1).max(50),
    title: z.string().min(1).max(160),
    detail: z.string().min(1).max(2000),
});
export const SeoPlaybookAlternativeSchema = z.object({
    title: z.string().min(1).max(160),
    whenToUse: z.string().min(1).max(600),
    tradeoff: z.string().min(1).max(600),
});
export const SeoPlaybookRecommendedActionSchema = z.object({
    decision: z.string().min(1).max(600),
    rationale: z.string().min(1).max(1200),
});
export const SeoPlaybookEstimateSchema = z.object({
    effort: SeoEffortSchema,
    timeMinutes: z.number().int().min(1).max(10_000),
});
export const SeoPlaybookSchema = z.object({
    recommendedAction: SeoPlaybookRecommendedActionSchema,
    steps: z.array(SeoPlaybookStepSchema).min(1).max(20),
    alternatives: z.array(SeoPlaybookAlternativeSchema).max(6),
    snippets: z.array(SeoPlaybookSnippetSchema).max(6),
    risks: z.string().max(1200).nullable(),
    estimate: SeoPlaybookEstimateSchema.nullable(),
});
export const SeoPlaybookQuerySchema = z.object({
    regenerate: z
        .union([z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0')])
        .optional(),
});
//# sourceMappingURL=seo.schema.js.map